import { api } from "@/api/client";
import { saveBlobResponse } from "@/lib/download";

/**
 * Журнал отгрузки — read-only разбор того, как собран и отгружен заказ
 * (право `order_journal`, бэкенд `order_journal/router.py`).
 *
 * Автор (`*_by`, `authors`, `user`) — логин портала; `null` у скана/фото до
 * 04.10.2026 значит «нет данных», а не «неизвестно кто».
 */

export type JournalOrderKind = "customer" | "transfer";
export type CheckStatus = "open" | "completed" | "cancelled";

export interface OrderCard {
  order_ref: string;
  kind: string;
  order_number: string;
  order_date: string | null;
  organization_name: string | null;
  kontragent_name: string | null;
  /** Статус ПОСЛЕДНЕЙ сессии проверки. */
  status: string;
  sessions_count: number;
  last_activity_at: string;
  completed_at: string | null;
  expense_number: string | null;
  pallets_total: number;
  pallets_shipped: number;
  /** Отгружены, но в портале не собирались (паллеты бота). */
  pallets_shipped_external: number;
  /** Паллеты и сканы отгрузки без грузоместа реестра (импорт паллет 1С не
   *  проставил): отгрузку к ним не приложить. 0 — норма. */
  without_unit: number;
}

export interface OrderListResponse {
  items: OrderCard[];
  has_more: boolean;
}

export interface SessionView {
  id: string;
  display_number: number | null;
  kind: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  opened_by: string | null;
  expense_ref: string | null;
  expense_number: string | null;
}

export interface PhotoMeta {
  id: string;
  created_at: string;
  taken_by: string | null;
  uploaded: boolean;
  content_type: string;
}

export interface ShipmentView {
  shipment_session_id: string;
  shipment_status: string;
  scanned_at: string;
  scanned_by: string | null;
  expense_ref: string | null;
  expense_number: string | null;
  expense_posted: boolean;
  expense_posted_at: string | null;
}

/** Ключ строки: `""` — характеристики/партии нет. */
export interface LineKey {
  nomenclature_ref: string;
  characteristic_ref: string;
  partia_ref: string;
}

export interface LineNames {
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  partia_name: string;
}

export interface PalletLine extends LineKey, LineNames {
  scans: number;
  qty_base: number;
  boxes: number;
  manual_scans: number;
  manual_qty_base: number;
  /** Сканы без данных об источнике и авторе (до 04.10.2026). */
  legacy_scans: number;
  first_scan_at: string | null;
  last_scan_at: string | null;
  authors: string[];
  matches: boolean;
}

export interface PalletView {
  id: string;
  session_id: string;
  session_display_number: number | null;
  position: number;
  label: string | null;
  status: string;
  /** Грузоместо реестра — ключ отгрузки; `null` — строка без грузоместа. */
  cargo_unit_id: string | null;
  /** Наш SSCC паллеты (18 цифр) — показывать группами (`groupSscc`). */
  sscc: string | null;
  /** Номер документа 1С у паллеты, импортированной из 1С (старая этикетка). */
  archive_number: string | null;
  /** Как назвать грузоместо, никогда не пусто: SSCC, иначе архивный номер 1С,
   *  иначе ведущая маркировка (SSCC упаковки и т. п.); `null` — нет грузоместа. */
  unit_label?: string | null;
  is_planned: boolean;
  printed_at: string | null;
  created_at: string;
  photos: PhotoMeta[];
  lines: PalletLine[];
  scans: number;
  qty_base: number;
  /** Коробов = мест-детей грузоместа; `null` — без грузоместа. */
  boxes: number | null;
  manual_scans: number;
  first_scan_at: string | null;
  last_scan_at: string | null;
  authors: string[];
  shipment: ShipmentView | null;
}

export interface ExternalShipped {
  /** Строка скана отгрузки — уникальный ключ строки списка. */
  id: string;
  /** `null` — скан отгрузки без грузоместа (к паллете не приложить). */
  cargo_unit_id: string | null;
  sscc: string | null;
  archive_number: string | null;
  unit_label?: string | null;
  /** Что записала отгрузка с этикетки (SSCC; у строк до реестра — номер 1С). */
  pallet_number: string;
  shipment: ShipmentView;
}

export interface FilterOption extends LineKey, LineNames {
  scans: number;
  qty_base: number;
  pallets: number;
}

export interface OrderHeader {
  order_ref: string;
  kind: string;
  order_number: string;
  order_date: string | null;
  organization_name: string | null;
  kontragent_name: string | null;
  comment: string | null;
}

export interface OrderJournal {
  header: OrderHeader;
  sessions: SessionView[];
  pallets: PalletView[];
  shipped_external: ExternalShipped[];
  filters: FilterOption[];
  applied_filter: LineKey | null;
  totals: { pallets: number; pallets_shipped: number; scans: number; qty_base: number };
  /** Строки без грузоместа: паллеты проверок и сканы отгрузки (они — в `shipped_external`). */
  pallets_without_unit: number;
  shipments_without_unit: number;
}

export interface ScanView {
  id: string;
  created_at: string;
  scanned_by: string | null;
  scan_kind: string;
  code: string;
  gtin: string | null;
  ean13: string | null;
  nomenclature_ref: string | null;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  unit_coefficient: number;
  qty_scanned: number;
  qty_base: number;
  /** scan | manual_qty | manual_code; `null` — до 04.10.2026. */
  qty_source: string | null;
}

export interface PalletScans {
  pallet_id: string;
  order_ref: string;
  cargo_unit_id: string | null;
  sscc: string | null;
  archive_number: string | null;
  unit_label?: string | null;
  label: string | null;
  scans: ScanView[];
}

export interface EventView {
  id: number;
  session_id: string;
  session_display_number: number | null;
  kind: string;
  created_at: string;
  user: string | null;
  pallet_id: string | null;
  item_id: string | null;
  payload: Record<string, unknown>;
}

export interface EventsResponse {
  items: EventView[];
  has_more: boolean;
}

export interface ReconciliationLine extends LineKey, LineNames {
  unit_coef: number;
  ordered: number;
  collected: number;
  shipped: number;
  not_in_order: boolean;
  collected_mismatch: boolean;
  shipped_mismatch: boolean;
}

export interface Reconciliation {
  order_ref: string;
  session_id: string;
  session_display_number: number | null;
  lines: ReconciliationLine[];
  ordered_total: number;
  collected_total: number;
  shipped_total: number;
  shipped_external_pallets: number;
  /** Паллеты и сканы отгрузки без грузоместа — «Отгружено» может быть занижено. */
  without_unit: number;
}

export interface FindHit {
  order_ref: string;
  order_number: string;
  kontragent_name: string | null;
  session_id: string;
  session_display_number: number | null;
  pallet_id: string;
  pallet_cargo_unit_id: string | null;
  pallet_sscc: string | null;
  pallet_archive_number: string | null;
  pallet_label: string | null;
  item_id: string;
  scanned_at: string;
  scanned_by: string | null;
  code: string;
  nomenclature_name: string | null;
  characteristic_name: string | null;
  partia_name: string | null;
  qty_base: number;
}

export interface FindResponse {
  code: string;
  hits: FindHit[];
  truncated: boolean;
}

export const ORDER_LIST_PAGE = 30;
export const EVENTS_PAGE = 1000;

export async function listJournalOrders(params: {
  q: string;
  date_from: string;
  date_to: string;
  offset: number;
}): Promise<OrderListResponse> {
  const { data } = await api.get<OrderListResponse>("/order-journal/orders", {
    params: {
      q: params.q || undefined,
      date_from: params.date_from || undefined,
      date_to: params.date_to || undefined,
      offset: params.offset,
      limit: ORDER_LIST_PAGE,
    },
  });
  return data;
}

/** Журнал заказа; фильтр по строке — на сервере (`nom` обязателен, пустые
 *  `char`/`partia` — «любая»). */
export async function getOrderJournal(
  orderRef: string,
  filter: LineKey | null,
): Promise<OrderJournal> {
  const { data } = await api.get<OrderJournal>(`/order-journal/orders/${orderRef}`, {
    params: filter
      ? {
          nom: filter.nomenclature_ref,
          char: filter.characteristic_ref || undefined,
          partia: filter.partia_ref || undefined,
        }
      : undefined,
  });
  return data;
}

export async function getPalletScans(palletId: string): Promise<PalletScans> {
  const { data } = await api.get<PalletScans>(`/order-journal/pallets/${palletId}/scans`);
  return data;
}

export async function getOrderEvents(
  orderRef: string,
  offset: number,
): Promise<EventsResponse> {
  const { data } = await api.get<EventsResponse>(`/order-journal/orders/${orderRef}/events`, {
    params: { offset, limit: EVENTS_PAGE },
  });
  return data;
}

export async function getReconciliation(orderRef: string): Promise<Reconciliation> {
  const { data } = await api.get<Reconciliation>(
    `/order-journal/orders/${orderRef}/reconciliation`,
  );
  return data;
}

export async function findInOrder(orderRef: string, code: string): Promise<FindResponse> {
  const { data } = await api.get<FindResponse>(`/order-journal/orders/${orderRef}/find`, {
    params: { code },
  });
  return data;
}

/** Фото паллеты (auth — Bearer) → object URL; вызывающий делает revoke. */
export async function fetchJournalPhotoUrl(
  orderRef: string,
  photoId: string,
  thumb: boolean,
): Promise<string> {
  const resp = await api.get<Blob>(`/order-journal/orders/${orderRef}/photos/${photoId}`, {
    params: thumb ? { size: "thumb" } : undefined,
    responseType: "blob",
  });
  return URL.createObjectURL(resp.data);
}

export async function downloadOrderJournalXlsx(orderRef: string): Promise<void> {
  const resp = await api.get<Blob>(`/order-journal/orders/${orderRef}/export.xlsx`, {
    responseType: "blob",
  });
  saveBlobResponse(resp, "Журнал_отгрузки.xlsx");
}
